import React from "react";
import {
  Instagram,
  Facebook,
  Phone,
  MapPin,
  Clock,
  ArrowRight,
} from "lucide-react";
import { RESTAURANT_INFO, LOGO_URL, QRIS_LOGO, BSI_LOGO } from "../constants";

const TikTokIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    stroke="currentColor"
    strokeWidth="2.5" // Menebal agar kontras lebih baik
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

interface FooterProps {
  onNavigate: (view: string) => void;
  onOpenLegal: (type: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate, onOpenLegal }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="relative overflow-hidden">
      {/* Brand Accent Line AT TOP */}
      <div
        className="h-2 bg-amber-400 w-full relative z-20"
        aria-hidden="true"
      ></div>

      <footer
        className="bg-[#ee1d23] text-white pt-16 relative overflow-hidden"
        role="contentinfo"
      >
        <div className="container mx-auto px-6 sm:px-8 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {/* --- UPDATE DI SINI: Background Putih & Ukuran Diperkecil --- */}
                {/* Menghapus bg-white, padding, shadow, dan rounded. Ukuran div diperkecil. */}
                <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                  <img
                    src={LOGO_URL}
                    // Menyesuaikan atribut lebar/tinggi eksplisit
                    width="40"
                    height="40"
                    // Memastikan gambar memenuhi kontainer tanpa distorsi
                    className="w-auto h-full object-contain"
                    alt="Logo Hisana Fried Chicken"
                  />
                </div>
                {/* -------------------------------------------------------- */}
                <h3 className="text-xl font-serif font-black leading-none">
                  HISANA
                  <br />
                  <span className="text-[11px] tracking-[0.2em] font-sans text-white uppercase">
                    {RESTAURANT_INFO.subName}
                  </span>
                </h3>
              </div>
              <p className="text-sm text-white leading-relaxed font-medium">
                Ayam goreng krispi pilihan keluarga Indonesia. Menyajikan
                kualitas terbaik dengan standar Halal terbaik sejak 2002.
              </p>
              <div className="flex gap-4">
                {/* Aria-label sudah ada, bagus! Tambahkan min-h/min-w untuk target sentuh */}
                <a
                  href={RESTAURANT_INFO.socials.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Kunjungi Instagram Hisana"
                  className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-amber-400 hover:text-[#ee1d23] transition-all"
                >
                  <Instagram size={22} />
                </a>
                <a
                  href={RESTAURANT_INFO.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Kunjungi Facebook Hisana"
                  className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-amber-400 hover:text-[#ee1d23] transition-all"
                >
                  <Facebook size={22} />
                </a>
                <a
                  href={RESTAURANT_INFO.socials.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Kunjungi TikTok Hisana"
                  className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-amber-400 hover:text-[#ee1d23] transition-all"
                >
                  <TikTokIcon />
                </a>
              </div>
            </div>

            {/* Navigation Column */}
            <nav className="space-y-6" aria-label="Navigasi Footer">
              <h4 className="text-lg font-bold border-b-2 border-amber-400 pb-2 inline-block">
                Navigasi
              </h4>
              <ul className="space-y-1">
                {[
                  { id: "beranda", label: "Beranda" },
                  { id: "menu", label: "Menu Krispi" },
                  { id: "galeri", label: "Galeri Momen" },
                  { id: "panduan", label: "Panduan Pesan" },
                  { id: "lokasi", label: "Lokasi Outlet" },
                ].map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => onNavigate(item.id)}
                      className="text-sm font-bold text-white hover:text-amber-400 hover:translate-x-2 transition-all flex items-center gap-2 py-2 min-h-[44px] w-full text-left"
                    >
                      <ArrowRight
                        size={14}
                        className="text-amber-400"
                        aria-hidden="true"
                      />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Contact Column */}
            <div className="space-y-6">
              <h4 className="text-lg font-bold border-b-2 border-amber-400 pb-2 inline-block">
                Hubungi Kami
              </h4>
              <div className="space-y-5">
                <div className="flex gap-4">
                  <MapPin
                    className="shrink-0 text-amber-400"
                    size={20}
                    aria-hidden="true"
                  />
                  <p className="text-sm text-white leading-relaxed font-semibold">
                    {RESTAURANT_INFO.address}
                  </p>
                </div>
                <div className="flex gap-4">
                  <Phone
                    className="shrink-0 text-amber-400"
                    size={20}
                    aria-hidden="true"
                  />
                  <p className="text-sm font-black text-white">
                    {RESTAURANT_INFO.phone}
                  </p>
                </div>
                <div className="flex gap-4">
                  <Clock
                    className="shrink-0 text-amber-400"
                    size={20}
                    aria-hidden="true"
                  />
                  <p className="text-sm font-bold text-white">
                    Setiap Hari: {RESTAURANT_INFO.hours} WIB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Certification & Payment Logos */}
          <div className="py-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center">
                {/* --- UPDATE DI SINI: Background Putih & Ukuran Diperkecil --- */}
                {/* Menghapus bg-white, shadow, border, rounded. Ukuran diperkecil dan menghapus padding. */}
                <div className="w-10 h-10 flex items-center justify-center mb-2 overflow-hidden">
                  <img
                    src={LOGO_URL}
                    // Menyesuaikan atribut lebar/tinggi eksplisit
                    width="40"
                    height="40"
                    // Memastikan gambar memenuhi kontainer tanpa distorsi
                    className="w-auto h-full object-contain"
                    alt="Logo Hisana Terverifikasi"
                  />
                </div>
                {/* -------------------------------------------------------- */}
                <span className="text-[10px] font-black uppercase tracking-widest text-white">
                  Terverifikasi
                </span>
              </div>
              <div className="h-12 w-px bg-white/30" aria-hidden="true"></div>
              <div className="flex flex-col items-start gap-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-400">
                  Metode Pembayaran
                </span>
                <div className="flex gap-5">
                  <img
                    src={QRIS_LOGO}
                    height="24"
                    className="h-6 w-auto brightness-200"
                    alt="QRIS"
                  />
                  <img
                    src={BSI_LOGO}
                    height="24"
                    className="h-6 w-auto brightness-200"
                    alt="Bank Syariah Indonesia"
                  />
                </div>
              </div>
            </div>

            <div className="text-center md:text-right">
              <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                © {currentYear} HISANA FRIED CHICKEN PADANG
              </p>
              <p className="text-[10px] font-bold text-white/70 mt-1 uppercase tracking-tighter">
                Professional Website by WebRancak
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
